<template>
  <div class="key-value-editor">
    <Form ref="tableFormRef" :model="formData" :rules="rules">
      <Table :columns="tableCol" :data="formData.tableData">
        <template slot-scope="{ index }" slot="key">
          <Form-item :prop="'tableData.' + index + '.name'" :rules="rules.name">
            <i-input
              v-model="formData.tableData[index].name"
              :disabled="disabled"
              :placeholder="formData.tableData[index].name.toString()"
            ></i-input>
          </Form-item>
        </template>

        <template slot-scope="{ row, index }" slot="value">
          <Form-item
            :prop="'tableData.' + index + '.default'"
            :rules="[
              { required: isDefaultValueRequired(row), trigger: 'blur', type: itemValueType(row.type, row.name) },
            ]"
          >
            <i-input
              v-if="row.type === 'int'"
              v-model.number="formData.tableData[index].default"
              :placeholder="formData.tableData[index].default.toString()"
              type="number"
              :disabled="disabled"
            ></i-input>
            <i-input
              v-else-if="row.type === 'string' && row.control === 'textarea'"
              v-model="formData.tableData[index].default"
              type="textarea"
            />
            <Select
              v-else-if="row.type === 'string' && row.control === 'select'"
              v-model="formData.tableData[index].default"
              clearable
              transfer
              @on-change="
                (value) => {
                  handleSelectChange(value, row.name)
                }
              "
            >
              <Option v-for="value in row.values" :key="value" :value="value">
                {{ value }}
              </Option>
            </Select>
            <i-switch v-else-if="row.type === 'bool'" v-model="formData.tableData[index].default" size="small" />
            <!-- adapt sql source -->
            <template v-else-if="row.type === 'string' && row.name === 'indexValue' && type.toLowerCase() === 'sql'">
              <i-input
                v-if="indexFieldType === 'DATETIME'"
                v-model="formData.tableData[index].default"
                :placeholder="formData.tableData[index].default.toString()"
                :disabled="disabled"
              />
              <i-input
                v-else-if="indexFieldType === 'INT'"
                v-model.number="formData.tableData[index].default"
                type="number"
                :placeholder="formData.tableData[index].default.toString()"
                :disabled="disabled"
              />
            </template>
            <i-input
              v-else
              v-model="formData.tableData[index].default"
              :placeholder="formData.tableData[index].default.toString()"
              :disabled="disabled"
            ></i-input>
          </Form-item>
        </template>

        <template v-if="!disabled" slot-scope="{ row, index }" slot="action">
          <Button shape="circle" type="error" size="small" ghost @click="deleteItem(index, row)">
            {{ $t('common.delete') }}
          </Button>
        </template>
      </Table>
    </Form>

    <Button v-if="!disabled" type="text" class="add-kv--btn" icon="md-add" ghost @click="addColumn">
      {{ $t('common.add') }}
    </Button>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import { dataType, propertiesValueType, setPropertyDefaultValue } from '@/utils/handleData'

export default {
  name: 'KeyValueEditor',

  model: {
    prop: 'value',
    event: 'update',
  },

  props: {
    value: {
      type: [Object, Array], // use with `properties`, when the value type is an `Object`
      default: () => {},
    },
    properties: {
      type: [Array, Object],
      default: null,
    },
    templateData: {
      type: [Object],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    modalVisible: {
      type: Boolean,
      default: false,
    },
    submitDataType: { type: String, default: 'list' }, // list || object
    type: { type: String, default: '' },
  },
  data() {
    return {
      tableCol: [
        {
          title: this.$t('common.key'),
          key: 'name',
          slot: 'key',
        },
        {
          title: this.$t('common.value'),
          key: 'default',
          slot: 'value',
        },
        {
          title: this.$t('common.operations'),
          width: 180,
          slot: 'action',
        },
      ],
      formData: {
        tableData: [],
      },
      indexFieldType: 'INT',
      sqlSpecialHandling: ['dateTimeFormat', 'indexField', 'indexFieldType', 'indexValue', 'indexFields'],
    }
  },
  computed: {
    rules() {
      return {
        name: [{ required: true, trigger: 'blur' }],
      }
    },
    valueType() {
      return dataType(this.value)
    },
    submitedDataType() {
      return this.submitDataType
    },
    itemValueType() {
      return (type, name) => {
        if (!type) return 'string'
        // adapt 'indexValue' of sql source
        if (this.type.toLowerCase() === 'sql' && name === 'indexValue') {
          return 'any'
        }
        const dataMap = new Map([
          ['int', 'number'],
          ['string', 'string'],
          ['bool', 'boolean'],
          ['default', 'string'],
        ])
        return dataMap.get(type) || dataMap.get('default')
      }
    },
    isDefaultValueRequired() {
      return (row) => {
        const { optional } = row
        let res = false
        if (optional !== undefined && optional !== null) {
          res = !optional
        }
        return res
      }
    },
    propertiesList() {
      const data = this.properties
      if (!data) return null

      let list = []
      if (dataType(data) === 'array') {
        list = data
      } else if (dataType(data) === 'object') {
        const kv = Object.values(data)
        kv.forEach((v) => {
          list.push(v)
        })
      }
      return list
    },
  },
  watch: {
    modalVisible(val) {
      if (val) {
        this.initDataToList(this.value)
      }
    },
    value: {
      immediate: true,
      handler(newV) {
        this.initDataToList(newV)
      },
      deep: true,
    },
    properties: {
      immediate: true,
      async handler() {
        this.initDataToList(this.value)
      },
    },
  },

  methods: {
    async initDataToList(value) {
      const data = cloneDeep(value || this.value)
      const propertiesData = this.propertiesList.filter((item) => this.sqlSpecialHandling.indexOf(item.name) < 0)

      if (this.valueType === 'array') {
        // exist `properties`
        if (propertiesData && propertiesData.length) {
          let resList = cloneDeep(propertiesData)

          // edit
          if (data?.length) {
            resList = this.mergeArrayValueIntoProperties(propertiesData, data)
          }

          this.formData.tableData = resList
          return
        }

        // if donot exist `properties`, directly use the value
        const resList = data || []
        this.formData.tableData = await this.parseArrayValue(resList)
        return
      }

      if (this.valueType === 'object') {
        const dataToList = this.parseJSONValueToList(data)
        if (propertiesData && propertiesData.length) {
          let resList = cloneDeep(propertiesData)

          if (data && Object.keys(data).length) {
            resList = this.mergeArrayValueIntoProperties(propertiesData, dataToList)
          }

          this.formData.tableData = resList
          return
        }

        // if donot exist `properties`, directly use the value
        this.formData.tableData = await this.parseArrayValue(dataToList)
      }
    },

    defaultItem(item) {
      const { name, default: defaultValue, type, control, optional } = item
      const newItem = {
        name,
        default: defaultValue,
        type: type || propertiesValueType(defaultValue),
        control: control || 'text',
        optional: optional || true,
      }
      return newItem
    },

    mergeArrayValueIntoProperties(properties, list) {
      if (!properties || !list || (!list.length && !properties.length)) return []
      if (!list.length) return properties

      const resList = cloneDeep(properties)

      list.forEach((item) => {
        const { name, default: defaultValue } = item
        const propertyIndex = resList.findIndex((p) => p.name === name)
        if (propertyIndex >= 0) {
          resList[propertyIndex].default = defaultValue
        } else if (this.sqlSpecialHandling.indexOf(name) < 0) {
          // remove 'indexFields' for sql source
          const newItem = this.defaultItem(item)
          resList.push(newItem)
        }
      })

      return resList
    },

    // list value
    parseArrayValue(list) {
      const resList = []

      if (list && Array.isArray(list) && list.length) {
        list.forEach((item) => {
          // remove 'indexFields' for sql source
          if (this.sqlSpecialHandling.indexOf(item.name) < 0) {
            const newItem = this.defaultItem(item)
            resList.push(newItem)
          }
        })
      }
      return Promise.resolve(resList)
    },

    // JSON value to List
    parseJSONValueToList(objectData) {
      if (!objectData || dataType(objectData) !== 'object') return []

      const resList = []
      const keys = Object.keys(objectData)
      keys.forEach((key) => {
        const value = objectData[key] || ''
        const item = {
          name: key,
          type: propertiesValueType(value) || 'string',
          default: value,
        }

        if (dataType(value) === 'object') {
          const { default: defaultValue, control, type } = value
          item.default = setPropertyDefaultValue(control, type, defaultValue)
        }
        // adapt sql source
        if (key === 'indexFieldType') {
          this.indexFieldType = item.default
        }

        resList.push(item)
      })
      return resList
    },

    // if you use `keyValueTreeEditor` component, data transfer must be triggered, to avoid errors when validate with other forms
    async transferData() {
      let resData = null
      if (this.submitedDataType.includes('list')) {
        resData = this.transferArrayDataToList(this.formData.tableData)
      }

      if (this.submitedDataType === 'object') {
        resData = this.transferArrayDataToJSON(this.formData.tableData)
      }
      this.$emit('update', resData)
      return Promise.resolve(resData)
    },

    // transfer data: transfer list data to new list, i.e. [{ name, default }]
    transferArrayDataToList(list) {
      if (!list || !Array.isArray(list) || !list.length) return []

      const resList = []
      list.forEach((item) => {
        const { name, default: defaultValue } = item
        const isAddItem = defaultValue !== '' && defaultValue !== null && defaultValue !== undefined
        if (isAddItem) {
          const newItem = {
            name,
            default: defaultValue,
          }
          resList.push(newItem)
        }
      })
      return resList
    },

    // transfer data: transfer list data to json: { ${key}: ${value}, }
    transferArrayDataToJSON(list) {
      if (!list || !Array.isArray(list) || !list.length) return {}

      const resJSON = {}
      list.forEach((item) => {
        const { name, default: defaultValue } = item
        const isAddItem = defaultValue !== '' && defaultValue !== null && defaultValue !== undefined
        if (isAddItem) {
          resJSON[name] = defaultValue
        }
        // adapt sql source
        if (name === 'indexValue') {
          resJSON[name] = this.indexFieldType === 'INT' ? Number(defaultValue) : defaultValue
        }
      })
      return resJSON
    },

    deleteItem(index) {
      this.formData.tableData.splice(index, 1)
    },
    addColumn() {
      this.formData.tableData.push({
        name: '',
        default: '',
        type: 'string',
      })
    },

    async validateKeyValueEditor() {
      try {
        const res = await this.$refs.tableFormRef.validate()
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },

    // adapt sql source
    handleSelectChange(val, name) {
      if (name === 'indexFieldType') {
        this.indexFieldType = val
        this.formData.tableData.forEach((item, index) => {
          if (item.name === 'indexValue') {
            this.formData.tableData[index].default = ''
          }
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.key-value-editor {
  width: 100%;
}
.add-kv--btn,
.add-kv--btn:hover {
  color: #34c388;
}
.add-kv--btn {
  padding-left: 0;
}
</style>
